import React, { ChangeEvent, Fragment, ReactElement, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Box,
    Backdrop,
    CircularProgress,
    Button,
    Typography,
    Grid2,
    Badge,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Menu,
    TextField,
    IconButton,
    Tooltip,
} from '@mui/material';
import { RootState } from '../../redux/reducers';
import { importOrders, refreshOrdersData } from '../../redux/actions/orderActions';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import OrdersTable from './OrdersTable';
import OrderForm from './forms/OrderForm';
import { CompleteOrderType, OrderStatusSR, OrderTypeSR } from '../../common/Types';
import { DetailsContainerBox, DetailsTableContainerBox } from '../../common/ResponsiveStyle';
import { OrderStatus } from '../../api/ClientApi';
import {
    OrderStatusFilterType,
    OrderTypeFilterType,
    filterDemandOrders,
    filterOrders,
    filterReturnOrders,
} from './OrdersHelper';
import { importClients } from '../../redux/actions/clientActions';
import { importSites } from '../../redux/actions/siteActions';
import {
    clearDemandOrderCollapsible,
    clearGeneralReturnOrderCollapsible,
    clearOrderCollapsible,
} from '../../redux/actions/collapsibleOpenStateActions';
import DemandOrderForm from './forms/DemandOrderForm';
import ReturnOrderForm from './forms/ReturnOrderForm';
import ExpressOrderForm from './forms/ExpressOrderForm';
import { importDemandOrders, refreshDemandOrdersData } from '../../redux/actions/demandOrderActions';
import { importReturnOrders, refreshReturnOrdersData } from '../../redux/actions/returnOrderActions';
import { importAllGoods } from '../../redux/actions/goodsActions';
import {
    isEmployeeProjectManager,
    isEmployeeWarehouseManager,
    isEmployeeWarehouseOwner,
    isEmployeeWarehouseOwnerOnly,
} from '../../redux/selectors/employeeSelector';

export default function OrdersList(): ReactElement {
    const orders = useAppSelector((state: RootState) => state.orders.orders);
    const returnOrders = useAppSelector((state: RootState) => state.returnOrders.returnOrders);
    const demandOrders = useAppSelector((state: RootState) => state.demandOrders.demandOrders);
    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const clients = useAppSelector((state: RootState) => state.clients.clients);
    const goods = useAppSelector((state: RootState) => state.goods.goods);

    const isHighestPrivilageWarehouseOwner = useAppSelector(isEmployeeWarehouseOwnerOnly);
    const isWarehouseOwner = useAppSelector(isEmployeeWarehouseOwner);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);

    const [orderTypeFilter, setOrderTypeFilter] = useState<OrderTypeFilterType>({
        Demand: false,
        Reception: false,
        GeneralReturn: false,
        Transfer: false,
    });

    const [orderStatusFilter, setOrderStatusFilter] = React.useState<OrderStatusFilterType>({
        Idle: false,
        Pending: false,
        Approved: false,
        Transport: false,
        Delivered: false,
        Returned: false,
        Cancelled: false,
    });

    const [orderOriginFilter, setOrderOriginFilter] = React.useState<string>('');
    const [orderDestinationFilter, setOrderDestinationFilter] = React.useState<string>('');
    const [orderClientFilter, setOrderClientFilter] = React.useState<string>('');

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!orders) {
            dispatch(importOrders());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!returnOrders) {
            dispatch(importReturnOrders());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!demandOrders) {
            dispatch(importDemandOrders());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!sites) {
            dispatch(importSites());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!clients) {
            dispatch(importClients());
        }
    }, [dispatch]);

    useEffect(() => {
        if (!goods) {
            dispatch(importAllGoods());
        }
    }, [dispatch]);

    //********  TIMER ********/

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(refreshOrdersData());
            dispatch(refreshDemandOrdersData());
            dispatch(refreshReturnOrdersData());
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [dispatch]);

    //******  END TIMER ******/

    useEffect(() => {
        dispatch(clearOrderCollapsible());
        dispatch(clearDemandOrderCollapsible());
        dispatch(clearGeneralReturnOrderCollapsible());
    }, [dispatch]);

    const handleOriginFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setOrderOriginFilter(value);
    };

    const handleDestinationFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setOrderDestinationFilter(value);
    };

    const handleClientFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setOrderClientFilter(value);
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const [anchorElOrderType, setAnchorElOrderType] = useState<null | HTMLElement>(null);

    const openOrderTypeFilter = Boolean(anchorElOrderType);

    const handleClickOrderTypeFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElOrderType(event.currentTarget);
    };
    const handleCloseOrderTypeFilter = () => {
        setAnchorElOrderType(null);
    };

    const handleOrderStatusFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        setOrderStatusFilter({
            ...orderStatusFilter,
            [e.target.name]: e.target.checked,
        });
    };

    const handleOrderTypeFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
        setOrderTypeFilter({
            ...orderTypeFilter,
            [e.target.name]: e.target.checked,
        });
    };

    const clearAllFilters = () => {
        setOrderTypeFilter({
            Demand: false,
            Reception: false,
            GeneralReturn: false,
            Transfer: false,
        });
        setOrderStatusFilter({
            Idle: false,
            Pending: false,
            Approved: false,
            Transport: false,
            Delivered: false,
            Returned: false,
            Cancelled: false,
        });
        setOrderOriginFilter('');
        setOrderDestinationFilter('');
    };

    const { Idle, Pending, Approved, Transport, Delivered, Returned, Cancelled } = orderStatusFilter;
    const { Demand, Reception, GeneralReturn, Transfer } = orderTypeFilter;

    const [openOrderForm, setOpenOrderForm] = useState<boolean>(false);
    const [openDemandOrderForm, setOpenDemandOrderForm] = useState<boolean>(false);
    const [openReturnOrderForm, setOpenReturnOrderForm] = useState<boolean>(false);
    const [openExpressOrderForm, setOpenExpressOrderForm] = useState<boolean>(false);

    const goToForm = () => {
        setOpenOrderForm(true);
    };

    const handleCloseOrderForm = () => {
        setOpenOrderForm(false);
    };

    const goToDemandOrderForm = () => {
        setOpenDemandOrderForm(true);
    };

    const handleCloseDemandOrderForm = () => {
        setOpenDemandOrderForm(false);
    };

    const goToReturnOrderForm = () => {
        setOpenReturnOrderForm(true);
    };

    const handleCloseReturnOrderForm = () => {
        setOpenReturnOrderForm(false);
    };

    const filteredOrders = filterOrders(
        orders ?? [],
        orderTypeFilter,
        orderStatusFilter,
        orderOriginFilter,
        orderDestinationFilter,
        orderClientFilter,
        clients ?? [],
        sites ?? [],
    );

    const filteredReturnOrders = filterReturnOrders(
        returnOrders ?? [],
        orderTypeFilter,
        orderStatusFilter,
        orderOriginFilter,
        orderClientFilter,
        clients ?? [],
        sites ?? [],
        isHighestPrivilageWarehouseOwner,
    );

    const filteredDemandOrders = filterDemandOrders(
        demandOrders ?? [],
        orderTypeFilter,
        orderStatusFilter,
        orderDestinationFilter,
        orderClientFilter,
        clients ?? [],
        sites ?? [],
        isHighestPrivilageWarehouseOwner,
    );

    // Only the orders gate the page; goods (the slowest list) fill in the availability marks later.
    return orders === null ? (
        <Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(orders)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Fragment>
    ) : (
        <DetailsContainerBox
            sx={{
                p: 2,
                backgroundColor: (theme) => (theme.palette.mode === 'light' ? 'white' : theme.palette.grey[900]),
            }}
        >
            <Grid2 container sx={{ py: 2, px: { xs: 2, sm: 4 }, justifyContent: 'space-between', gap: 2 }}>
                <Grid2 sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5">Nalozi</Typography>
                    <Tooltip title="Osveži">
                        <IconButton
                            color="primary"
                            aria-label="Osveži"
                            onClick={() => {
                                dispatch(importOrders());
                                dispatch(importDemandOrders());
                                dispatch(importReturnOrders());
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Grid2>
                <Grid2>
                    <Box display={'flex'} gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
                        {(isProjectManager || isWarehouseOwner) && (
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={goToReturnOrderForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Nova povratnica
                            </Button>
                        )}
                        {(isProjectManager || isWarehouseOwner) && (
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={goToDemandOrderForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Novo trebovanje
                            </Button>
                        )}
                        {(isProjectManager || isWarehouseManager) && (
                            <Button
                                color="warning"
                                variant="outlined"
                                onClick={() => setOpenExpressOrderForm(true)}
                                startIcon={<BoltIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Ubrzani prenos
                            </Button>
                        )}
                        {!isHighestPrivilageWarehouseOwner && (
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={goToForm}
                                startIcon={<AddIcon />}
                                sx={{ textTransform: 'none' }}
                            >
                                Novi nalog
                            </Button>
                        )}
                    </Box>
                </Grid2>
            </Grid2>
            <hr />
            <Box sx={{ p: { xs: 1, sm: 4 }, pt: 2 }}>
                <Grid2 container sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexDirection: 'start' }}>
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: { xs: 130, sm: 200 } }}
                        placeholder="Izvor"
                        value={orderOriginFilter}
                        onChange={handleOriginFilterChange}
                    />
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: { xs: 130, sm: 200 } }}
                        placeholder="Odredište"
                        value={orderDestinationFilter}
                        onChange={handleDestinationFilterChange}
                    />
                    <TextField
                        size="small"
                        sx={{ mb: 1, width: { xs: 130, sm: 200 } }}
                        placeholder="Klijent"
                        value={orderClientFilter}
                        onChange={handleClientFilterChange}
                    />
                    <Box sx={{ width: { xs: 130, sm: 200 } }}>
                        <Badge
                            badgeContent={[Demand, GeneralReturn, Transfer, Reception].filter((s) => s).length}
                            color="warning"
                        >
                            <Button
                                id="basic-button"
                                aria-controls={openOrderTypeFilter ? 'basic-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={openOrderTypeFilter ? 'true' : undefined}
                                variant="outlined"
                                sx={{ width: { xs: 130, sm: 200 } }}
                                onClick={handleClickOrderTypeFilter}
                            >
                                Vrsta
                            </Button>
                        </Badge>
                        <Menu
                            id="basic-menu"
                            anchorEl={anchorElOrderType}
                            open={openOrderTypeFilter}
                            onClose={handleCloseOrderTypeFilter}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                        >
                            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                <FormGroup>
                                    {Object.values(CompleteOrderType).map((ot) => {
                                        return (
                                            <FormControlLabel
                                                key={ot}
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            ot === CompleteOrderType.Transfer
                                                                ? Transfer
                                                                : ot === CompleteOrderType.Reception
                                                                  ? Reception
                                                                  : ot === CompleteOrderType.Demand
                                                                    ? Demand
                                                                    : ot === CompleteOrderType.GeneralReturn
                                                                      ? GeneralReturn
                                                                      : false
                                                        }
                                                        onChange={handleOrderTypeFilterChange}
                                                        name={ot}
                                                    />
                                                }
                                                label={OrderTypeSR[ot]}
                                            />
                                        );
                                    })}
                                </FormGroup>
                            </FormControl>
                        </Menu>
                    </Box>
                    <Box sx={{ width: { xs: 130, sm: 200 } }}>
                        <Badge
                            badgeContent={
                                [Idle, Pending, Approved, Transport, Delivered, Returned, Cancelled].filter((s) => s)
                                    .length
                            }
                            color="warning"
                        >
                            <Button
                                id="basic-button"
                                aria-controls={open ? 'basic-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                variant="outlined"
                                sx={{ width: { xs: 130, sm: 200 } }}
                                onClick={handleClick}
                            >
                                Status
                            </Button>
                        </Badge>
                        <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                        >
                            <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                                <FormGroup>
                                    {Object.values(OrderStatus).map((os) => {
                                        return (
                                            <FormControlLabel
                                                key={os}
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            os === OrderStatus.Idle
                                                                ? Idle
                                                                : os === OrderStatus.Pending
                                                                  ? Pending
                                                                  : os === OrderStatus.Approved
                                                                    ? Approved
                                                                    : os === OrderStatus.Transport
                                                                      ? Transport
                                                                      : os === OrderStatus.Delivered
                                                                        ? Delivered
                                                                        : os === OrderStatus.Returned
                                                                          ? Returned
                                                                          : os === OrderStatus.Cancelled
                                                                            ? Cancelled
                                                                            : false
                                                        }
                                                        onChange={handleOrderStatusFilterChange}
                                                        name={os}
                                                    />
                                                }
                                                label={OrderStatusSR[os]}
                                            />
                                        );
                                    })}
                                </FormGroup>
                            </FormControl>
                        </Menu>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box>
                        <Button onClick={clearAllFilters}>Očisti filtere</Button>
                    </Box>
                </Grid2>
                <Box sx={{ p: 0, mt: 2 }}></Box>

                <DetailsTableContainerBox>
                    <OrdersTable
                        orders={filteredOrders ?? null}
                        returnOrders={filteredReturnOrders}
                        demandOrders={filteredDemandOrders}
                    />
                </DetailsTableContainerBox>
            </Box>
            <OrderForm
                closeForm={handleCloseOrderForm}
                open={openOrderForm}
                orderId={undefined}
                originOrderType={CompleteOrderType.Transfer}
            />
            <DemandOrderForm
                closeForm={handleCloseDemandOrderForm}
                open={openDemandOrderForm}
                demandOrderId={undefined}
            />
            <ReturnOrderForm
                closeForm={handleCloseReturnOrderForm}
                open={openReturnOrderForm}
                returnOrderId={undefined}
            />
            <ExpressOrderForm closeForm={() => setOpenExpressOrderForm(false)} open={openExpressOrderForm} />
        </DetailsContainerBox>
    );
}
